import { DesktopWindowsOutlined, PhoneIphoneOutlined } from '@mui/icons-material'
import { Stack, Typography } from '@mui/material'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FlexBox, GapBox } from 'components/Box'
import { BoxForm, NumberField, Switch } from 'components/Form'
import { SectionHeader } from 'components/Header'
import { Link } from 'components/Link'
import Paper from 'components/Paper'
import { useAbility } from 'providers/casl'
import { URL } from '../../constants'
import { Schema } from '../../schema'
import { RefreshIntervalSoftValidator } from './RefreshIntervalSoftValidator'

export const BasicSettingsSection = (): JSX.Element => {
  const ability = useAbility()
  const { t } = useTranslation('features/domain', { keyPrefix: 'form.refresher.basicSettings' })
  const { t: tc } = useTranslation('common')
  const { control } = useFormContext<Schema>()

  const cannotUpdateDomain: boolean = ability.cannot('update', 'Domain')

  return (
    <Paper data-cy='basic-settings-section'>
      <SectionHeader
        subtitle={t('subtitle')}
        subtitleEndAdornment={
          <Link
            newTab
            to={URL.KNOWLEDGE_BASE.BASIC_SETTINGS}
          >
            {tc('learnMore')}
          </Link>
        }
        title={t('title')}
      />

      <GapBox />

      <Stack
        direction='row'
        spacing={8}
      >
        <BoxForm>
          <FlexBox
            axis='x'
            sx={{ gap: 1 }}
          >
            <DesktopWindowsOutlined
              data-cy='desktop-windows-outlined-icon'
              fontSize='inherit'
            />
            <Typography>{tc('device.desktop')}</Typography>
          </FlexBox>

          <NumberField
            control={control}
            disabled={cannotUpdateDomain}
            helperText={RefreshIntervalSoftValidator<Schema>({
              path: 'desktopRefreshInterval',
            })}
            label={t('columns.refreshInterval.label')}
            name='desktopRefreshInterval'
            tooltip={{ content: t('columns.refreshInterval.tooltip') }}
            width='md'
          />

          <NumberField
            control={control}
            disabled={cannotUpdateDomain}
            fixedHelperText
            label={t('columns.adThreshold.label')}
            name='desktopAdThreshold'
            tooltip={{ content: t('columns.adThreshold.tooltip') }}
            width='md'
          />

          <Switch
            control={control}
            disabled={cannotUpdateDomain}
            label={t('columns.monitorUserScrollEnabled.label')}
            labelPlacement='start'
            name='desktopMonitorUserScrollEnabled'
            tooltip={{ content: t('columns.monitorUserScrollEnabled.tooltip') }}
          />
        </BoxForm>

        <BoxForm>
          <FlexBox
            axis='x'
            sx={{ gap: 1 }}
          >
            <PhoneIphoneOutlined
              data-cy='phone-iphone-outlined-icon'
              fontSize='inherit'
            />
            <Typography>{tc('device.mobile')}</Typography>
          </FlexBox>

          <NumberField
            control={control}
            disabled={cannotUpdateDomain}
            helperText={RefreshIntervalSoftValidator<Schema>({
              path: 'mobileRefreshInterval',
            })}
            label={t('columns.refreshInterval.label')}
            name='mobileRefreshInterval'
            tooltip={{ content: t('columns.refreshInterval.tooltip') }}
            width='md'
          />

          <NumberField
            control={control}
            disabled={cannotUpdateDomain}
            fixedHelperText
            label={t('columns.adThreshold.label')}
            name='mobileAdThreshold'
            tooltip={{ content: t('columns.adThreshold.tooltip') }}
            width='md'
          />

          <Switch
            control={control}
            disabled={cannotUpdateDomain}
            label={t('columns.monitorUserScrollEnabled.label')}
            labelPlacement='start'
            name='mobileMonitorUserScrollEnabled'
            tooltip={{ content: t('columns.monitorUserScrollEnabled.tooltip') }}
          />
        </BoxForm>
      </Stack>
    </Paper>
  )
}
